/**
 * @fileoverview Syndicator Showdown — Admin Non-Runner Declaration Panel
 * @module components/admin/NonRunnerPanel
 *
 * Allows admin to declare a runner a non-runner after race completion
 * due to a steward ruling. Sits inside AdminResultsPage on settled races.
 *
 * Use case: horse ran but stewards subsequently declared it a non-runner
 * (e.g. barrier incident, interference, failed vet check post-race).
 */

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions(undefined, 'australia-southeast1');
const declareNonRunnerFn = httpsCallable(functions, 'declareNonRunner');

export const NonRunnerPanel = ({ race, slateId }) => {
  const [selectedRunner, setSelectedRunner] = useState('');
  const [reason,         setReason]         = useState('');
  const [status,         setStatus]         = useState('idle'); // idle | loading | success | error
  const [resultMsg,      setResultMsg]       = useState('');
  const [confirming,     setConfirming]      = useState(false);

  const runners = (race.runners || []).filter(
    r => r.status?.toLowerCase() !== 'scratched'
  );

  const handleDeclare = async () => {
    if (!selectedRunner || !reason.trim()) return;
    setStatus('loading');
    try {
      const result = await declareNonRunnerFn({
        slateId,
        raceId:   race.id,
        runnerId: selectedRunner,
        reason:   reason.trim(),
      });
      setResultMsg(result.data.message);
      setStatus('success');
      setSelectedRunner('');
      setReason('');
      setConfirming(false);
    } catch (err) {
      setResultMsg(`Error: ${err.message}`);
      setStatus('error');
    }
  };

  return (
    <div className="ss-nr-panel">
      <div className="ss-nr-header">
        <span className="ss-nr-icon">⚖️</span>
        <div>
          <h4 className="ss-nr-title">Steward Ruling — Declare Non-Runner</h4>
          <p className="ss-nr-desc">
            Use only when stewards officially declare a runner a non-runner
            after the race. Affected player equity will be voided and refunded.
            The race result for all other runners is unchanged.
          </p>
        </div>
      </div>

      {status === 'success' ? (
        <div className="ss-nr-success">✓ {resultMsg}</div>
      ) : (
        <>
          <div className="ss-nr-fields">
            <div className="ss-nr-field">
              <label className="ss-nr-label">Runner</label>
              <select
                className="ss-nr-select"
                value={selectedRunner}
                onChange={e => { setSelectedRunner(e.target.value); setConfirming(false); }}
              >
                <option value="">— Select runner —</option>
                {runners.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.runnerNumber}. {r.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ss-nr-field">
              <label className="ss-nr-label">Official Reason</label>
              <input
                type="text"
                className="ss-nr-input"
                value={reason}
                onChange={e => { setReason(e.target.value); setConfirming(false); }}
                placeholder="e.g. Declared non-runner by stewards — barrier incident"
                maxLength={200}
              />
            </div>
          </div>

          {!confirming ? (
            <button
              className="ss-nr-btn ss-nr-btn--warn"
              onClick={() => setConfirming(true)}
              disabled={!selectedRunner || !reason.trim() || status === 'loading'}
              type="button"
            >
              Declare Non-Runner
            </button>
          ) : (
            <div className="ss-nr-confirm-block">
              <p className="ss-nr-confirm-warning">
                ⚠ This will void and refund all player equity in{' '}
                <strong>{runners.find(r => r.id === selectedRunner)?.name || selectedRunner}</strong>.
                This cannot be undone.
              </p>
              <div className="ss-nr-confirm-actions">
                <button
                  className="ss-nr-btn ss-nr-btn--danger"
                  onClick={handleDeclare}
                  disabled={status === 'loading'}
                  type="button"
                >
                  {status === 'loading' ? '⏳ Processing…' : '✓ Confirm Declaration'}
                </button>
                <button
                  className="ss-nr-btn ss-nr-btn--cancel"
                  onClick={() => setConfirming(false)}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="ss-nr-error">⚠ {resultMsg}</div>
          )}
        </>
      )}
    </div>
  );
};

export default NonRunnerPanel;
